import { SoundManager, SoundType } from '../audio/SoundManager';

/**
 * Event handlers for DeathUI
 */
export interface DeathUIEvents {
  onRespawn: () => void;
  onQuit: () => void;
}

/**
 * Death statistics
 */
export interface DeathStats {
  timeAlive?: number; // seconds
  foodEaten?: number;
  maxLength?: number;
  kills?: number;
  earnedPoints?: number; // Points earned from kills
}

/**
 * DeathUI - Arcade-style death screen with animations
 *
 * Features:
 * - Animated entrance with fade and scale
 * - Pulsing "Game Over" title
 * - Stats display (score, time, food eaten)
 * - Animated skull icon
 * - Quick respawn (1 second delay)
 * - Colorful, arcade aesthetic
 */
export class DeathUI {
  /** Container element */
  private container: HTMLDivElement;

  /** Content wrapper */
  private content: HTMLDivElement;

  /** Skull icon */
  private skullIcon: HTMLDivElement;

  /** Title element */
  private titleElement: HTMLHeadingElement;

  /** Subtitle element */
  private subtitleElement: HTMLParagraphElement;

  /** Stats container */
  private statsContainer: HTMLDivElement;

  /** Respawn button */
  private respawnButton: HTMLButtonElement;

  /** Quit button */
  private quitButton: HTMLButtonElement;

  /** Countdown element */
  private countdownElement: HTMLSpanElement;

  /** Event handlers */
  private events: DeathUIEvents;

  /** Whether UI is visible */
  private visible: boolean = false;

  /** Countdown timer */
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private respawnCountdown: number = 0;

  /** Animation frame ID */
  private animationFrame: number | null = null;

  constructor(events: DeathUIEvents) {
    this.events = events;

    // Inject keyframe animations
    this.injectStyles();

    // Create container with backdrop blur
    this.container = document.createElement('div');
    this.container.id = 'death-ui';
    this.container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: none;
      justify-content: center;
      align-items: center;
      background: radial-gradient(ellipse at center, rgba(40, 10, 30, 0.95) 0%, rgba(10, 5, 15, 0.98) 100%);
      backdrop-filter: blur(8px);
      z-index: 1000;
      font-family: 'Segoe UI', Arial, sans-serif;
      opacity: 0;
      transition: opacity 0.3s ease-out;
    `;

    // Content wrapper with scale animation
    this.content = document.createElement('div');
    this.content.style.cssText = `
      text-align: center;
      max-width: 450px;
      padding: 40px 50px;
      background: linear-gradient(145deg, rgba(60, 20, 50, 0.6), rgba(30, 10, 25, 0.8));
      border-radius: 24px;
      border: 2px solid rgba(255, 100, 100, 0.3);
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(255, 80, 80, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1);
      transform: scale(0.8) translateY(20px);
      transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    `;

    // Animated skull icon
    this.skullIcon = document.createElement('div');
    this.skullIcon.innerHTML = '💀';
    this.skullIcon.style.cssText = `
      font-size: 64px;
      margin-bottom: 10px;
      animation: death-skull-bounce 0.6s ease-in-out infinite alternate;
      filter: drop-shadow(0 4px 20px rgba(255, 100, 100, 0.5));
    `;
    this.content.appendChild(this.skullIcon);

    // Game Over title with gradient and animation
    this.titleElement = document.createElement('h1');
    this.titleElement.textContent = 'GAME OVER';
    this.titleElement.style.cssText = `
      font-size: 42px;
      font-weight: 900;
      letter-spacing: 4px;
      margin: 0 0 8px 0;
      background: linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 50%, #ffaaaa 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      text-shadow: none;
      animation: death-title-pulse 1.5s ease-in-out infinite;
      filter: drop-shadow(0 4px 20px rgba(255, 107, 107, 0.6));
    `;
    this.content.appendChild(this.titleElement);

    // Subtitle (killed by / boundary)
    this.subtitleElement = document.createElement('p');
    this.subtitleElement.style.cssText = `
      color: #cc8888;
      font-size: 16px;
      margin: 0 0 25px 0;
      opacity: 0.9;
    `;
    this.content.appendChild(this.subtitleElement);

    // Stats container
    this.statsContainer = document.createElement('div');
    this.statsContainer.style.cssText = `
      display: flex;
      justify-content: center;
      gap: 20px;
      margin-bottom: 30px;
      flex-wrap: wrap;
    `;
    this.content.appendChild(this.statsContainer);

    // Countdown element
    this.countdownElement = document.createElement('span');
    this.countdownElement.style.cssText = `
      display: block;
      color: #888;
      font-size: 13px;
      margin-bottom: 15px;
      min-height: 20px;
    `;
    this.content.appendChild(this.countdownElement);

    // Button container
    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
      display: flex;
      justify-content: center;
      gap: 15px;
    `;

    // Respawn button with glow effect
    this.respawnButton = document.createElement('button');
    this.respawnButton.textContent = '🔄 RESPAWN';
    this.respawnButton.style.cssText = `
      padding: 16px 36px;
      font-size: 18px;
      font-weight: bold;
      letter-spacing: 1px;
      border: none;
      border-radius: 12px;
      background: linear-gradient(135deg, #4ecdc4 0%, #44b8a8 100%);
      color: #fff;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: 0 4px 15px rgba(78, 205, 196, 0.4), 0 0 20px rgba(78, 205, 196, 0.2);
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    `;
    this.respawnButton.addEventListener('click', () => {
      if (!this.respawnButton.disabled) {
        SoundManager.getInstance().play(SoundType.UI_SUCCESS);
        this.events.onRespawn();
      }
    });
    this.respawnButton.addEventListener('mouseover', () => {
      if (!this.respawnButton.disabled) {
        SoundManager.getInstance().play(SoundType.UI_HOVER);
        this.respawnButton.style.transform = 'translateY(-3px) scale(1.02)';
        this.respawnButton.style.boxShadow = '0 8px 25px rgba(78, 205, 196, 0.5), 0 0 30px rgba(78, 205, 196, 0.3)';
      }
    });
    this.respawnButton.addEventListener('mouseout', () => {
      this.respawnButton.style.transform = 'translateY(0) scale(1)';
      if (!this.respawnButton.disabled) {
        this.respawnButton.style.boxShadow = '0 4px 15px rgba(78, 205, 196, 0.4), 0 0 20px rgba(78, 205, 196, 0.2)';
      }
    });
    buttonContainer.appendChild(this.respawnButton);

    // Quit button
    this.quitButton = document.createElement('button');
    this.quitButton.textContent = '🚪 QUIT';
    this.quitButton.style.cssText = `
      padding: 16px 28px;
      font-size: 16px;
      font-weight: 600;
      letter-spacing: 1px;
      border: 2px solid rgba(150, 100, 100, 0.4);
      border-radius: 12px;
      background: rgba(80, 40, 40, 0.3);
      color: #aa8888;
      cursor: pointer;
      transition: all 0.2s ease;
    `;
    this.quitButton.addEventListener('click', () => {
      SoundManager.getInstance().play(SoundType.UI_CLICK);
      this.events.onQuit();
    });
    this.quitButton.addEventListener('mouseover', () => {
      SoundManager.getInstance().play(SoundType.UI_HOVER);
      this.quitButton.style.borderColor = 'rgba(200, 100, 100, 0.6)';
      this.quitButton.style.background = 'rgba(100, 50, 50, 0.4)';
      this.quitButton.style.color = '#cc9999';
      this.quitButton.style.transform = 'translateY(-2px)';
    });
    this.quitButton.addEventListener('mouseout', () => {
      this.quitButton.style.borderColor = 'rgba(150, 100, 100, 0.4)';
      this.quitButton.style.background = 'rgba(80, 40, 40, 0.3)';
      this.quitButton.style.color = '#aa8888';
      this.quitButton.style.transform = 'translateY(0)';
    });
    buttonContainer.appendChild(this.quitButton);

    this.content.appendChild(buttonContainer);
    this.container.appendChild(this.content);
    document.body.appendChild(this.container);
  }

  /**
   * Inject CSS keyframe animations
   */
  private injectStyles(): void {
    if (document.getElementById('death-ui-styles')) return;

    const style = document.createElement('style');
    style.id = 'death-ui-styles';
    style.textContent = `
      @keyframes death-skull-bounce {
        0% { transform: translateY(0) rotate(-5deg); }
        100% { transform: translateY(-8px) rotate(5deg); }
      }
      @keyframes death-title-pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.9; transform: scale(1.02); }
      }
      @keyframes death-stat-pop {
        0% { transform: scale(0); opacity: 0; }
        70% { transform: scale(1.1); }
        100% { transform: scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Create a stat box element
   */
  private createStatBox(icon: string, label: string, value: string, color: string, delay: number): HTMLDivElement {
    const box = document.createElement('div');
    box.style.cssText = `
      background: rgba(0, 0, 0, 0.3);
      border-radius: 12px;
      padding: 12px 18px;
      min-width: 80px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      animation: death-stat-pop 0.4s ease-out ${delay}ms both;
    `;

    const iconEl = document.createElement('div');
    iconEl.textContent = icon;
    iconEl.style.cssText = `font-size: 24px; margin-bottom: 4px;`;
    box.appendChild(iconEl);

    const valueEl = document.createElement('div');
    valueEl.textContent = value;
    valueEl.style.cssText = `
      font-size: 22px;
      font-weight: bold;
      color: ${color};
      text-shadow: 0 2px 10px ${color}44;
    `;
    box.appendChild(valueEl);

    const labelEl = document.createElement('div');
    labelEl.textContent = label;
    labelEl.style.cssText = `font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 1px;`;
    box.appendChild(labelEl);

    return box;
  }

  /**
   * Show the death screen
   */
  show(killedBy: string | null, finalScore: number, respawnDelayMs: number = 1000, stats?: DeathStats): void {
    this.visible = true;
    this.container.style.display = 'flex';

    // Trigger animations after display
    requestAnimationFrame(() => {
      this.container.style.opacity = '1';
      this.content.style.transform = 'scale(1) translateY(0)';
    });

    // Update subtitle
    if (killedBy) {
      this.subtitleElement.innerHTML = `Devoured by <span style="color:#ff8888;font-weight:bold;">${killedBy}</span>`;
    } else {
      this.subtitleElement.textContent = 'You crashed into the boundary!';
    }

    // Build stats
    this.statsContainer.innerHTML = '';
    let statDelay = 100;

    // Score stat (always show)
    this.statsContainer.appendChild(
      this.createStatBox('⭐', 'Score', this.formatScore(finalScore), '#ffd700', statDelay)
    );
    statDelay += 80;

    // Time alive
    if (stats?.timeAlive !== undefined) {
      this.statsContainer.appendChild(
        this.createStatBox('⏱️', 'Time', this.formatTime(stats.timeAlive), '#4ecdc4', statDelay)
      );
      statDelay += 80;
    }

    // Food eaten
    if (stats?.foodEaten !== undefined) {
      this.statsContainer.appendChild(
        this.createStatBox('🍎', 'Food', stats.foodEaten.toString(), '#ff6b9d', statDelay)
      );
      statDelay += 80;
    }

    // Max length
    if (stats?.maxLength !== undefined) {
      this.statsContainer.appendChild(
        this.createStatBox('📏', 'Length', stats.maxLength.toString(), '#a78bfa', statDelay)
      );
      statDelay += 80;
    }

    // Kills
    if (stats?.kills !== undefined && stats.kills > 0) {
      this.statsContainer.appendChild(
        this.createStatBox('💀', 'Kills', stats.kills.toString(), '#ff6b6b', statDelay)
      );
      statDelay += 80;
    }

    // Earned points from kills
    if (stats?.earnedPoints !== undefined && stats.earnedPoints > 0) {
      this.statsContainer.appendChild(
        this.createStatBox('💰', 'Earned', this.formatScore(stats.earnedPoints), '#00ff88', statDelay)
      );
    }

    // Setup respawn countdown (reduced to 1 second)
    this.respawnCountdown = Math.ceil(respawnDelayMs / 1000);
    this.updateCountdown();
    this.setButtonDisabled(true);

    // Clear any existing interval
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    // Start countdown
    this.countdownInterval = setInterval(() => {
      this.respawnCountdown--;
      this.updateCountdown();

      if (this.respawnCountdown <= 0) {
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }
        this.setButtonDisabled(false);
      }
    }, 1000);
  }

  /**
   * Set button disabled state
   */
  private setButtonDisabled(disabled: boolean): void {
    this.respawnButton.disabled = disabled;
    if (disabled) {
      this.respawnButton.style.opacity = '0.5';
      this.respawnButton.style.cursor = 'not-allowed';
      this.respawnButton.style.boxShadow = 'none';
    } else {
      this.respawnButton.style.opacity = '1';
      this.respawnButton.style.cursor = 'pointer';
      this.respawnButton.style.boxShadow = '0 4px 15px rgba(78, 205, 196, 0.4), 0 0 20px rgba(78, 205, 196, 0.2)';
      this.respawnButton.style.animation = 'death-title-pulse 1s ease-in-out infinite';
    }
  }

  /**
   * Update countdown display
   */
  private updateCountdown(): void {
    if (this.respawnCountdown > 0) {
      this.countdownElement.textContent = `Respawn in ${this.respawnCountdown}...`;
    } else {
      this.countdownElement.innerHTML = '<span style="color:#4ecdc4;">Ready to respawn!</span>';
    }
  }

  /**
   * Hide the death screen with animation
   */
  hide(): void {
    this.visible = false;

    // Animate out
    this.container.style.opacity = '0';
    this.content.style.transform = 'scale(0.9) translateY(-20px)';

    setTimeout(() => {
      this.container.style.display = 'none';
    }, 300);

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  /**
   * Check if visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Format score for display
   */
  private formatScore(score: number): string {
    if (score >= 1000000) {
      return (score / 1000000).toFixed(1) + 'M';
    } else if (score >= 1000) {
      return (score / 1000).toFixed(1) + 'K';
    }
    return score.toString();
  }

  /**
   * Format time for display
   */
  private formatTime(seconds: number): string {
    if (seconds >= 60) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${Math.floor(seconds)}s`;
  }

  /**
   * Clean up
   */
  destroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    this.container.remove();
  }
}
